import { useState, useRef, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import gsap from 'gsap';
import { Globe } from 'lucide-react';
import { FilmStrip, Palette, Waveform, UsersThree, Export } from '@phosphor-icons/react';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { AUTH_COLORS } from '@/constants/theme';
import { useAuth } from '../hooks/useAuth';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { AppleSignInButton } from '../components/AppleSignInButton';
import { switchLanguage } from '@/utils/langTransition';
import { useAppBranding } from '@/hooks/useAppBranding';
import { FloatingField } from '@/components/ui';
import { platform } from '@/utils/platform';

/* ─── Electric floor glow (GSAP) ───────────── */

interface ElectricFloorHandle { play: () => void; stop: () => void }
interface ElectricFloorProps { className?: string; style?: React.CSSProperties }

const ElectricFloor = forwardRef<ElectricFloorHandle, ElectricFloorProps>(function ElectricFloor({ className, style }, ref) {
  const svgRef = useRef<SVGSVGElement>(null);
  const sessionRef = useRef<gsap.core.Timeline | null>(null);

  const play = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const ring = svg.querySelector('.floor-ring') as SVGEllipseElement;
    if (!ring) return;

    sessionRef.current?.kill();

    gsap.set(ring, { opacity: 0, attr: { 'stroke-width': 0.8 } });

    const createPulseTrain = () => {
      const tl = gsap.timeline();
      for (let i = 0; i < 36; i++) {
        tl.to(ring, {
          opacity: 0.85,
          attr: { 'stroke-width': 2.5 },
          duration: 0.02,
          ease: 'expo.out',
          yoyo: true,
          repeat: 1,
        }, i * 0.055);
      }
      return tl;
    };

    const session = gsap.timeline({ repeat: -1, repeatDelay: 4 });
    session.add(createPulseTrain());
    sessionRef.current = session;
  }, []);

  const stop = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    sessionRef.current?.kill();
    sessionRef.current = null;
    const ring = svg.querySelector('.floor-ring') as SVGEllipseElement;
    if (ring) gsap.set(ring, { opacity: 0, attr: { 'stroke-width': 0.8 } });
  }, []);

  useImperativeHandle(ref, () => ({ play, stop }), [play, stop]);

  useEffect(() => {
    return () => { sessionRef.current?.kill(); };
  }, []);

  return (
    <svg
      ref={svgRef}
      className={className ?? "absolute -bottom-3 left-1/2 -translate-x-1/2"}
      style={{ width: '95%', height: '45px', overflow: 'visible', ...style }}
      viewBox="0 0 200 50"
      preserveAspectRatio="none"
    >
      <ellipse cx="100" cy="25" rx="98" ry="22"
        className="floor-ring"
        fill="none"
        stroke="rgba(120,200,255,0.7)"
        strokeWidth="0.8"
        opacity="0"
        style={{ filter: 'drop-shadow(0 0 2px rgba(120,200,255,0.9))' }}
      />
      <defs>
        <radialGradient id="loginFloorGrad">
          <stop offset="0%" stopColor="rgba(255,255,255,0.15)" />
          <stop offset="50%" stopColor="rgba(255,255,255,0.08)" />
          <stop offset="70%" stopColor="transparent" />
        </radialGradient>
      </defs>
    </svg>
  );
});

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const error = searchParams.get('error');
  const { login, loginLoading, loginError } = useAuth();
  const { companyName, appName, loginSubtitle } = useAppBranding();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [pulseActive, setPulseActive] = useState(false);
  const [powerLevel, setPowerLevel] = useState(2); // 0=80%MT, 1=100%MT, 2=120%MT(MDD), 3=130%MT
  const powerLevels = [
    { label: '80%', sublabel: 'Mapping', mt: 0.8 },
    { label: '100%', sublabel: 'OCD', mt: 1.0 },
    { label: '120%', sublabel: 'MDD', mt: 1.2 },
    { label: '130%', sublabel: 'Max', mt: 1.3 },
  ];
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const isRtl = i18n.dir() === 'rtl';

  const heroImgRef = useRef<HTMLImageElement>(null);
  const floorRef = useRef<ElectricFloorHandle>(null);
  const imgSessionRef = useRef<gsap.core.Timeline | null>(null);
  const circuitPulseRef = useRef<SVGPathElement>(null);
  const circuitTrackRef = useRef<SVGPathElement>(null);
  const circuitSessionRef = useRef<gsap.core.Timeline | null>(null);
  const logoIconRef = useRef<HTMLDivElement>(null);
  const brandWrapperRef = useRef<HTMLDivElement>(null);
  const [brandHeight, setBrandHeight] = useState(800);

  const startPulse = useCallback((mt?: number) => {
    const img = heroImgRef.current;
    const pulse = circuitPulseRef.current;
    if (!img || !pulse) return;

    const power = mt ?? powerLevels[powerLevel].mt;
    const brightness = 1 + (0.8 * power);          // 1.64 → 2.04
    const glowSize = Math.round(16 * power);       // 13 → 21
    const boltWidth = 1.5 + (4 * power);            // 2.7 → 6.7
    const boltSegment = 0.04;                       // shorter segment for longer path

    const pathLength = pulse.getTotalLength();
    gsap.set(img, { opacity: 0, filter: 'brightness(1)' });
    const boltGlow = `drop-shadow(0 0 ${2 + power * 10}px rgba(120,200,255,${0.4 + power * 0.4})) drop-shadow(0 0 ${4 + power * 20}px rgba(120,200,255,${0.1 + power * 0.3})) drop-shadow(0 0 ${8 + power * 30}px rgba(80,160,255,${0.05 + power * 0.2}))`;
    gsap.set(pulse, {
      strokeDasharray: `${pathLength * boltSegment} ${pathLength * 3}`,
      strokeDashoffset: pathLength * 1.08,
      opacity: 0,
      attr: { 'stroke-width': boltWidth, stroke: `rgba(${Math.round(80 + power * 80)},${Math.round(180 + power * 50)},255,${0.6 + power * 0.35})` },
      filter: boltGlow,
    });

    // H7 Coil Protocol: 20 Hz, 2s trains of 40 pulses — intensity scales with power
    const createPulseTrain = () => {
      const tl = gsap.timeline();
      for (let i = 0; i < 40; i++) {
        tl.to(img, {
          filter: `brightness(${brightness}) drop-shadow(0 0 ${glowSize}px rgba(120,200,255,${0.3 + 0.5 * power}))`,
          opacity: 1,
          duration: 0.025,
          ease: 'power2.in',
        })
        .to(img, {
          filter: 'brightness(1)',
          opacity: 0.3,
          duration: 0.025,
          ease: 'power2.out',
        });
      }
      tl.to(img, { opacity: 0, duration: 0.4, ease: 'power2.out' });
      return tl;
    };

    const logo = logoIconRef.current;
    const track = circuitTrackRef.current;
    const logoBaseFilter = 'drop-shadow(0 0 6px rgba(120,200,255,0.4)) drop-shadow(0 0 12px rgba(120,200,255,0.2))';
    const logoChargeFilter = `drop-shadow(0 0 ${8 + power * 12}px rgba(120,200,255,${0.6 + power * 0.3})) drop-shadow(0 0 ${16 + power * 20}px rgba(120,200,255,${0.4 + power * 0.2})) drop-shadow(0 0 ${24 + power * 28}px rgba(80,160,255,${0.2 + power * 0.15}))`;
    const logoBurstFilter = `drop-shadow(0 0 ${20 + power * 25}px rgba(200,230,255,${0.8 + power * 0.2})) drop-shadow(0 0 ${35 + power * 35}px rgba(120,200,255,${0.6 + power * 0.3})) drop-shadow(0 0 ${50 + power * 40}px rgba(80,160,255,${0.3 + power * 0.2}))`;
    const boltDuration = 1.8 - (0.4 * power);
    const restDuration = 5 - power;

    const runCycle = () => {
      const session = gsap.timeline({
        onComplete: runCycle, // recursive loop
      });

      // Phase 0: Logo charges up
      if (logo) {
        session.to(logo, { opacity: 0.6, filter: logoChargeFilter, duration: 0.6, ease: 'power2.in' });
        for (let i = 0; i < 8; i++) {
          session.to(logo, { opacity: 0.9, filter: logoBurstFilter, duration: 0.03, ease: 'power2.in' })
                 .to(logo, { opacity: 0.5, filter: logoChargeFilter, duration: 0.03, ease: 'power2.out' });
        }
        // BURST — bolt fires at the same instant as the burst
        session.set(pulse, { opacity: 1, strokeDashoffset: pathLength });
        if (track) session.set(track, { opacity: 1 });
        session.to(logo, { opacity: 1, filter: logoBurstFilter, scale: 1.08, duration: 0.08, ease: 'expo.out' });
        session.to(pulse, { strokeDashoffset: -(pathLength * 0.96), duration: boltDuration, ease: 'none' }, `<`);
        // Logo fades back after burst
        session.to(logo, { opacity: 0.3, filter: logoBaseFilter, scale: 1, duration: 0.5, ease: 'power2.out' });
      } else {
        session.set(pulse, { opacity: 1, strokeDashoffset: pathLength });
        if (track) session.set(track, { opacity: 1 });
        session.to(pulse, { strokeDashoffset: -(pathLength * 0.96), duration: boltDuration, ease: 'none' });
      }

      // Hide bolt + track immediately after it arrives
      session.set(pulse, { opacity: 0 });
      if (track) session.set(track, { opacity: 0 });

      // Phase 2: Pulse train
      session.add(createPulseTrain());

      // Phase 4: Rest
      session.set({}, {}, `+=${restDuration}`);

      imgSessionRef.current = session;
      circuitSessionRef.current = session;
    };

    runCycle();
  }, [powerLevel]);

  const stopPulse = useCallback(() => {
    imgSessionRef.current?.kill();
    imgSessionRef.current = null;
    circuitSessionRef.current?.kill();
    circuitSessionRef.current = null;
    const img = heroImgRef.current;
    if (img) {
      gsap.killTweensOf(img);
      gsap.set(img, { opacity: 0, filter: 'none' });
    }
    const pulse = circuitPulseRef.current;
    if (pulse) {
      gsap.killTweensOf(pulse);
      gsap.set(pulse, { opacity: 0 });
    }
    const track = circuitTrackRef.current;
    if (track) gsap.set(track, { opacity: 0 });
    const logo = logoIconRef.current;
    if (logo) {
      gsap.killTweensOf(logo);
      gsap.set(logo, { opacity: 0.3, filter: 'drop-shadow(0 0 6px rgba(120,200,255,0.4)) drop-shadow(0 0 12px rgba(120,200,255,0.2))', scale: 1 });
    }
  }, []);

  const togglePulse = useCallback(() => {
    setPulseActive(prev => {
      if (prev) { stopPulse(); } else { startPulse(); }
      return !prev;
    });
  }, [startPulse, stopPulse]);

  const handlePowerChange = useCallback((level: number) => {
    setPowerLevel(level);
    if (pulseActive) {
      stopPulse();
      // Restart with new power after a tick
      setTimeout(() => {
        startPulse(powerLevels[level].mt);
      }, 50);
    }
  }, [pulseActive, stopPulse, startPulse]);

  // Measure brand wrapper height for circuit SVG
  useEffect(() => {
    const el = brandWrapperRef.current;
    if (!el) return;
    const measure = () => setBrandHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      imgSessionRef.current?.kill();
      circuitSessionRef.current?.kill();
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[Login] handleSubmit fired, email:', email, 'password length:', password.length);
    login({ email, password, remember });
  };

  const loginErrorData = (loginError as { response?: { data?: { message?: string; pendingApproval?: boolean } } })?.response?.data;
  const errorMessage = loginError && !loginErrorData?.pendingApproval
    ? loginErrorData?.message || t('auth.loginFailed')
    : null;

  const pendingApproval = reason === 'pending_approval';
  const pendingName = searchParams.get('name');

  const glassInputStyle = {
    backgroundColor: 'var(--auth-page-input-bg)',
    backdropFilter: 'blur(8px)',
    border: '1px solid var(--auth-page-input-border)',
    color: 'var(--auth-page-input-text)',
  };

  const isDev = import.meta.env.VITE_APP_ENV === 'development';

  const langToggle = (
    <div className="flex justify-end" style={{ direction: 'ltr' }}>
      <button
        type="button"
        onClick={() => switchLanguage(i18n, i18n.language === 'he' ? 'en' : 'he', false)}
        className="lang-toggle flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold active:scale-95 cursor-pointer"
        style={{
          backgroundColor: 'var(--auth-page-input-bg)',
          backdropFilter: 'blur(8px)',
          border: '1px solid var(--auth-page-input-border)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          color: 'var(--auth-page-heading)',
        }}
      >
        <Globe size={14} />
        {i18n.language === 'he' ? 'English' : 'עברית'}
      </button>
    </div>
  );

  const formContent = (
    <>
      {/* Language toggle + DEV badge */}
      <div className="flex items-center justify-between" style={{ direction: 'ltr' }}>
        {isDev ? (
          <span className="rounded-md bg-amber-500 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white">
            DEV
          </span>
        ) : <span />}
        {langToggle}
      </div>

      {/* Header */}
      <div className="text-center">
        <div
          className="login-logo mx-auto h-20 aspect-[3/2] mb-2 cursor-default"
          role="img"
          aria-label={appName}
          style={{
            background: `linear-gradient(145deg, var(--auth-page-heading) 0%, var(--auth-page-accent) 50%, var(--auth-page-heading) 100%)`,
            maskImage: 'url(/images/vidnight-logo-white.png)',
            WebkitMaskImage: 'url(/images/vidnight-logo-white.png)',
            maskSize: 'contain',
            WebkitMaskSize: 'contain',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.2))',
          }}
        />
        <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: 'var(--auth-page-heading)' }}>{appName}</h1>
        <p className="mt-2 text-sm font-medium" style={{ color: 'var(--auth-page-text)', opacity: 0.7 }}>
          {loginSubtitle}
        </p>
      </div>

      {/* Session expired / refresh failed notice */}
      {reason === 'session_expired' && (
        <div
          className="rounded-xl p-3 text-sm font-semibold"
          style={{
            backgroundColor: AUTH_COLORS.warningBg,
            border: '1px solid var(--auth-page-card-border)',
            color: AUTH_COLORS.warningText,
          }}
        >
          {t('auth.sessionExpired')}
        </div>
      )}
      {reason === 'refresh_failed' && (
        <div
          className="rounded-xl p-3 text-sm font-semibold"
          style={{
            backgroundColor: AUTH_COLORS.warningBg,
            border: '1px solid var(--auth-page-card-border)',
            color: AUTH_COLORS.warningText,
          }}
        >
          {t('auth.refreshFailed')}
        </div>
      )}

      {/* Google auth failed */}
      {error === 'auth_failed' && (
        <div
          className="rounded-xl p-3 text-sm font-bold"
          style={{
            backgroundColor: AUTH_COLORS.errorBg,
            border: '1px solid var(--auth-page-card-border)',
            color: AUTH_COLORS.errorText,
          }}
        >
          {t('auth.googleAuthFailed')}
        </div>
      )}

      {/* Error */}
      {errorMessage && (
        <div
          className="rounded-xl p-3 text-sm font-bold"
          style={{
            backgroundColor: AUTH_COLORS.errorBg,
            border: '1px solid var(--auth-page-card-border)',
            color: AUTH_COLORS.errorText,
          }}
        >
          {errorMessage}
        </div>
      )}

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <FloatingField
          inputId="login-field"
          label={t('auth.email')}
          name="login-field"
          type="text"
          inputMode="text"
          autoComplete="off"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          inputClassName="glass-input"
          inputStyle={glassInputStyle}
          inputDir="ltr"
        />

        <FloatingField
          inputId="password"
          label={t('auth.password')}
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          inputClassName="glass-input"
          inputStyle={glassInputStyle}
          inputDir="ltr"
        />

        <div className="flex items-center justify-between">
          <label
            className="flex items-center gap-2 text-sm"
            style={{ color: 'var(--auth-page-text)' }}
          >
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded"
              style={{
                accentColor: 'var(--auth-page-accent)',
                backgroundColor: 'var(--auth-page-input-bg)',
                borderColor: 'var(--auth-page-input-border)',
              }}
            />
            {t('auth.rememberMe')}
          </label>
          <Link
            to="/forgot-password"
            className="login-link text-sm font-bold"
            style={{ color: 'var(--auth-page-text)' }}
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>

        <button
          type="submit"
          disabled={loginLoading}
          className="login-submit w-full rounded-xl py-3 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: 'var(--auth-page-button-bg)',
            color: 'var(--auth-page-button-text)',
            border: '1px solid var(--auth-page-card-border)',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          }}
        >
          {loginLoading ? t('common.loading') : t('auth.login')}
        </button>
      </form>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full" style={{ borderTop: '1px solid var(--auth-page-card-border)' }} />
        </div>
        <div className="relative flex justify-center text-sm">
          <span
            className="px-4 rounded-full"
            style={{
              backgroundColor: 'var(--auth-page-card-bg)',
              color: 'var(--auth-page-text)',
              opacity: 0.7,
            }}
          >
            {t('auth.orContinueWith')}
          </span>
        </div>
      </div>

      {/* Google Sign-In */}
      <div className="social-btn-wrap">
        <GoogleSignInButton
          onSuccess={() => {}}
          onError={(error) => console.error('Google sign-in error:', error)}
        />
      </div>
      <div className="social-btn-wrap">
        <AppleSignInButton
          onSuccess={() => {}}
          onError={(error) => console.error('Apple sign-in error:', error)}
        />
      </div>

      {/* Register Link */}
      <p className="text-center text-sm" style={{ color: 'var(--auth-page-text)', opacity: 0.7 }}>
        {t('auth.noAccount')}{' '}
        <Link to="/register" className="login-link font-bold" style={{ color: 'var(--auth-page-heading)' }}>
          {t('auth.register')}
        </Link>
      </p>
    </>
  );

  // ─── Pending Approval: rich informational page ─────────────────
  if (pendingApproval) {
    const welcomeText = pendingName
      ? t('auth.pendingWelcome', { name: pendingName })
      : t('auth.pendingWelcomeGeneric');

    const pendingContent = (
      <div
        className={`${isMobile ? 'p-2' : 'rounded-3xl p-6 md:p-8'} space-y-5 text-center`}
        style={isMobile ? {} : {
          backgroundColor: 'var(--auth-page-card-bg)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid var(--auth-page-card-border)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
      >
        {/* Language toggle */}
        <div className="flex items-center justify-between">
          {isDev ? (
            <span className="rounded-md bg-amber-500 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white">
              DEV
            </span>
          ) : <span />}
          {langToggle}
        </div>

        {/* Logo + App Title */}
        <div>
          <img
            src="/images/vidnight_logo_square.png"
            alt={appName}
            className="mx-auto mb-2 h-[84px] w-[84px] rounded-xl"
            style={{ animation: 'logo-glow 2.5s ease-in-out infinite alternate' }}
          />
          <style>{`
            @keyframes logo-glow {
              from { filter: brightness(1); }
              to { filter: brightness(1.8) saturate(0.3); }
            }
          `}</style>
          <h2 className="text-xl font-extrabold tracking-tight" style={{ color: 'var(--auth-page-heading)' }}>
            {appName}
          </h2>
          <p className="text-xs font-medium" style={{ color: 'var(--auth-page-text)' }}>
            {loginSubtitle}
          </p>
        </div>

        {/* Illustration */}
        <img
          src="/images/pending_approval.png"
          alt="Pending approval"
          className="mx-auto w-full max-w-[360px] rounded-2xl"
        />

        {/* Welcome heading */}
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: 'var(--auth-page-heading)' }}>
            {welcomeText}
          </h1>
          <p className="mt-1 text-sm font-medium" style={{ color: 'var(--auth-page-text)' }}>
            {t('auth.pendingSubmitted')}
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--auth-page-text)' }}>
            {t('auth.pendingNextSteps')}
          </p>
        </div>

        {/* Back to login */}
        <p className="text-sm" style={{ color: 'var(--auth-page-text)', opacity: 0.7 }}>
          <Link to="/login" className="font-bold" style={{ color: 'var(--auth-page-heading)' }}>
            {t('auth.backToLogin')}
          </Link>
        </p>
      </div>
    );

    if (isMobile) {
      return (
        <div className="login-mesh-bg fixed inset-0" style={{ position: 'fixed' }}>
          <div
            className="relative z-10 h-full overflow-y-auto flex flex-col justify-center items-center px-5 py-8 space-y-5"
            style={{
              paddingTop: platform.isAndroid ? '2rem' : 'calc(env(safe-area-inset-top, 0px) + 2rem)',
              backgroundColor: 'var(--auth-page-bg)',
              backdropFilter: 'blur(20px) saturate(180%)',
            }}
          >
            {pendingContent}
          </div>
        </div>
      );
    }

    return (
      <div className="login-mesh-bg flex min-h-dvh items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="relative z-10 w-full max-w-md"
        >
          {pendingContent}
        </motion.div>
      </div>
    );
  }

  // ─── Mobile: fullscreen full-bleed with neon glow ─────────────────
  if (isMobile) {
    return (
      <div className="login-mesh-bg fixed inset-0" style={{ position: 'fixed' }}>
        <div
          className="relative z-10 h-full overflow-y-auto flex flex-col justify-center px-5 py-8"
          style={{
            paddingTop: platform.isAndroid ? '2rem' : 'calc(env(safe-area-inset-top, 0px) + 2rem)',
            backgroundColor: 'var(--auth-page-bg)',
            backdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          <div
            className="neon-card mx-auto w-full max-w-md space-y-6 rounded-3xl p-6"
            style={{
              backgroundColor: 'var(--auth-page-card-bg)',
              backdropFilter: 'blur(20px) saturate(180%)',
            }}
          >
            {formContent}
          </div>
        </div>
        <style>{neonGlowStyles}</style>
      </div>
    );
  }

  // ─── Desktop: login card + brand drawer ────────────────
  return (
    <div className="login-mesh-bg fixed inset-0 flex items-center justify-center" style={{ position: 'fixed' }}>
      {/* Combined card + drawer unit */}
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        dir="ltr"
        className="relative flex items-stretch"
      >
        {/* Login card — primary, full height, on top (z-10) */}
        <div
          className="neon-card relative z-10 w-[440px] shrink-0 space-y-6 rounded-3xl p-8"
          style={{
            backgroundColor: 'var(--auth-page-card-bg)',
            backdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          {formContent}
        </div>

        {/* Brand drawer — notebook cover with top spiral binding */}
        <div
          ref={brandWrapperRef}
          className="relative w-[420px] shrink-0"
          style={{
            marginTop: 70,
            marginBottom: 70,
            marginLeft: -16,
          }}
        >
          {/* The cover itself */}
          <div className="notebook-cover relative flex flex-col justify-center overflow-hidden">
            {/* Grain overlay */}
            <div className="notebook-grain" />

            <div dir="auto" className="relative z-[1] px-8 py-8">
              {/* Vidnight logo — glass gradient masked by logo shape */}
              <div className="flex justify-center mb-4">
                <div
                  ref={logoIconRef}
                  style={{ width: 72, height: 48, opacity: 0.3, filter: 'drop-shadow(0 0 6px rgba(120,200,255,0.4)) drop-shadow(0 0 12px rgba(120,200,255,0.2))' }}
                >
                  <div
                    className="w-full h-full"
                    style={{
                      background: `linear-gradient(145deg, var(--auth-page-accent) 0%, rgba(255,255,255,0.7) 45%, var(--auth-page-accent) 55%, rgba(255,255,255,0.6) 100%)`,
                      maskImage: 'url(/images/vidnight-logo-white.png)',
                      WebkitMaskImage: 'url(/images/vidnight-logo-white.png)',
                      maskSize: 'contain',
                      WebkitMaskSize: 'contain',
                      maskRepeat: 'no-repeat',
                      WebkitMaskRepeat: 'no-repeat',
                      maskPosition: 'center',
                      WebkitMaskPosition: 'center',
                    }}
                  />
                </div>
              </div>
              {/* Welcome heading — embossed look */}
              <h2 className="notebook-emboss text-2xl font-extrabold tracking-tight text-center mb-1">
                {t('auth.welcomeTitle')}
              </h2>
              <p className="text-sm text-center max-w-sm mx-auto mb-6" style={{ color: 'var(--auth-page-text)', opacity: 0.7 }}>
                {t('auth.welcomeSubtitle')}
              </p>

              {/* Feature list */}
              <div className="space-y-3 mb-24 w-full max-w-sm mx-auto">
                {[
                  { icon: FilmStrip, key: 'auth.feature1' },
                  { icon: Palette, key: 'auth.feature2' },
                  { icon: Waveform, key: 'auth.feature3' },
                  { icon: UsersThree, key: 'auth.feature4' },
                  { icon: Export, key: 'auth.feature5' },
                ].map(({ icon: FeatureIcon, key }) => (
                  <div key={key} className="feature-item flex items-center gap-3">
                    <div
                      className="feature-icon-circle flex items-center justify-center shrink-0"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.15)',
                      }}
                    >
                      <FeatureIcon size={16} weight="fill" style={{ color: 'var(--auth-page-accent)' }} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--auth-page-text)' }}>{t(key)}</span>
                  </div>
                ))}
              </div>

            </div>

            {/* Floor — behind content */}
            <div
              className="absolute bottom-0 left-0 right-0 z-0"
              style={{
                height: '90px',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 100%)',
                borderRadius: '0 0 20px 0',
                borderTop: '1.5px solid var(--auth-page-accent)',
                boxShadow: '0 -4px 12px color-mix(in srgb, var(--auth-page-accent) 35%, transparent), 0 -1px 4px color-mix(in srgb, var(--auth-page-accent) 50%, transparent)',
              }}
            />
          </div>

          {/* Circuit SVG — logo → right edge → down → floor → left to illustration */}
          {(() => {
            const floorY = brandHeight - 90;
            const circuitD = `M 250,56 L 398,56 Q 410,56 410,68 L 410,${floorY - 12} Q 410,${floorY} 398,${floorY} L 160,${floorY}`;
            return (
              <svg
                className="absolute inset-0 z-0 pointer-events-none"
                style={{ width: 420, height: brandHeight, overflow: 'visible' }}
                viewBox={`0 0 420 ${brandHeight}`}
              >
                {/* Dim track */}
                <path
                  ref={circuitTrackRef}
                  d={circuitD}
                  fill="none"
                  stroke="rgba(30,80,180,0.15)"
                  strokeWidth="1.5"
                  opacity="0"
                />
                {/* Neon pulse bolt */}
                <path
                  ref={circuitPulseRef}
                  d={circuitD}
                  fill="none"
                  stroke="rgba(120,200,255,0.9)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  opacity="0"
                />
              </svg>
            );
          })()}
        </div>
      </motion.div>
      <style>{neonGlowStyles}</style>
    </div>
  );
}

// ── Neon line + notebook styles ──
const neonGlowStyles = `
  /* ── Notebook Cover (themed) ── */
  .notebook-cover {
    background: linear-gradient(
      145deg,
      color-mix(in srgb, var(--auth-page-card-bg) 80%, var(--auth-page-accent)) 0%,
      var(--auth-page-card-bg) 40%,
      color-mix(in srgb, var(--auth-page-card-bg) 90%, black) 100%
    );
    border-radius: 0 20px 20px 0;
    border: 1px solid var(--auth-page-card-border);
    border-left: none;
    position: relative;
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.1),
      inset 0 -1px 0 rgba(0,0,0,0.15),
      4px 6px 20px rgba(0,0,0,0.15);
  }

  /* Spine — darker reinforced strip on the left edge */
  .notebook-cover::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 18px;
    height: 100%;
    background: linear-gradient(90deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.08) 60%, transparent 100%);
    border-radius: 0;
    pointer-events: none;
  }

  /* Subtle grain texture overlay */
  .notebook-grain {
    position: absolute;
    inset: 0;
    border-radius: 0 20px 20px 0;
    opacity: 0.04;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4'%3E%3Crect width='1' height='1' fill='%23fff' x='0' y='0'/%3E%3Crect width='1' height='1' fill='%23fff' x='2' y='2'/%3E%3C/svg%3E");
    background-size: 4px 4px;
    pointer-events: none;
    z-index: 0;
  }

  /* Embossed / letterpress text effect */
  .notebook-emboss {
    color: var(--auth-page-heading);
    text-shadow:
      1px 1px 0px rgba(0,0,0,0.3),
      -0.5px -0.5px 0px rgba(255,255,255,0.1);
  }


  /* ── Hover & animation effects ── */

  /* Logo shimmer on hover */
  .login-logo {
    transition: filter 0.3s ease;
  }
  .login-logo:hover {
    filter: drop-shadow(0 4px 12px rgba(0,0,0,0.25)) brightness(1.15);
  }

  /* Inputs glow on focus */
  .glass-input {
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
  }
  .glass-input:focus {
    border-color: var(--auth-page-accent) !important;
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--auth-page-accent) 15%, transparent), 0 0 12px color-mix(in srgb, var(--auth-page-accent) 20%, transparent);
  }
  .glass-input:hover:not(:focus) {
    border-color: color-mix(in srgb, var(--auth-page-accent) 30%, transparent) !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }

  /* Submit button hover glow */
  .login-submit {
    transition: box-shadow 0.3s ease, filter 0.3s ease;
  }
  .login-submit:hover:not(:disabled) {
    box-shadow: 0 6px 24px rgba(0,0,0,0.3), 0 0 16px color-mix(in srgb, var(--auth-page-button-bg) 40%, transparent) !important;
    filter: brightness(1.1);
  }

  /* Links underline slide-in */
  .login-link {
    position: relative;
    transition: color 0.2s ease;
  }
  .login-link::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 0;
    height: 1.5px;
    background: var(--auth-page-accent);
    transition: width 0.3s ease;
  }
  .login-link:hover::after {
    width: 100%;
  }
  .login-link:hover {
    filter: brightness(1.2);
  }

  /* Language toggle hover */
  .lang-toggle {
    transition: box-shadow 0.3s ease, background-color 0.3s ease, filter 0.3s ease;
  }
  .lang-toggle:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.12) !important;
    filter: brightness(1.1);
  }

  /* Feature list items hover */
  .feature-item {
    transition: background-color 0.3s ease;
    padding: 6px 8px;
    margin: -6px -8px;
    border-radius: 12px;
  }
  .feature-item:hover {
    background-color: rgba(255,255,255,0.08);
  }
  .feature-item .feature-icon-circle {
    transition: background-color 0.3s ease, box-shadow 0.3s ease;
  }
  .feature-item:hover .feature-icon-circle {
    background-color: rgba(255,255,255,0.18) !important;
    box-shadow: 0 0 10px color-mix(in srgb, var(--auth-page-accent) 30%, transparent);
  }

  /* Power toggle button */
  .power-toggle {
    transition: filter 0.2s ease, opacity 0.2s ease;
    opacity: 0.85;
    background: none;
    border: none;
    padding: 0;
  }
  .power-toggle:hover {
    opacity: 1;
    filter: drop-shadow(0 0 6px rgba(120,200,255,0.5));
  }


  /* Notebook cover hover */
  .notebook-cover {
    transition: box-shadow 0.4s ease;
  }
  .notebook-cover:hover {
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.1),
      inset 0 -1px 0 rgba(0,0,0,0.15),
      6px 10px 30px rgba(0,0,0,0.22) !important;
  }

  /* Social buttons hover */
  .social-btn-wrap > button {
    transition: box-shadow 0.3s ease, filter 0.3s ease !important;
  }
  .social-btn-wrap > button:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.12) !important;
    filter: brightness(1.05);
  }

  .neon-card {
    border: 1px solid color-mix(in srgb, var(--auth-page-accent) 15%, transparent);
    box-shadow:
      0 0 3px color-mix(in srgb, var(--auth-page-accent) 12%, transparent),
      0 0 8px color-mix(in srgb, var(--auth-page-accent) 6%, transparent),
      0 0 20px color-mix(in srgb, var(--auth-page-accent) 3%, transparent);
    animation: neon-breathe 3s ease-in-out infinite;
  }

  @keyframes neon-breathe {
    0%, 100% {
      box-shadow:
        0 0 3px color-mix(in srgb, var(--auth-page-accent) 12%, transparent),
        0 0 8px color-mix(in srgb, var(--auth-page-accent) 6%, transparent),
        0 0 20px color-mix(in srgb, var(--auth-page-accent) 3%, transparent);
    }
    50% {
      box-shadow:
        0 0 4px color-mix(in srgb, var(--auth-page-accent) 18%, transparent),
        0 0 10px color-mix(in srgb, var(--auth-page-accent) 10%, transparent),
        0 0 25px color-mix(in srgb, var(--auth-page-accent) 5%, transparent);
    }
  }
`;
